/**
 * FogOfWarRenderer — presentation half of fog of war (team 0 = the player).
 *
 * The sibling-owned `src/sim/vision.ts` computes visibility; this module only
 * presents it:
 *   - `upload()` copies the vision RGBA texture into `world.visibilityTex`
 *     (consumed by the ground shader in world.ts). Call after each vision
 *     recompute (~2Hz), NOT every frame.
 *   - `syncEntities()` toggles entity visibility from vision state. Cheap
 *     enough per frame: last visibility is cached per object and only
 *     toggled on change.
 *
 * Presentation rules:
 *   - Team 0 (player) entities: always visible.
 *   - Team 1 fighters/workers: shown only while currently visible to team 0.
 *   - Team 1 buildings: hidden until explored; explored-but-unseen renders as
 *     a dim "ghost" (per-view materials — see note below).
 *   - Resource nodes are terrain features: shown once explored, stay visible.
 *
 * Shared-materials note: `createBuildingView` builds a NEW
 * MeshStandardMaterial per view, so dimming `bv.mesh.material` here cannot
 * leak onto other buildings. When a ghost becomes visible again the
 * canonical visual state is restored via `updateBuildingView(v)`.
 *
 * Worker hiding uses zero-scale instance matrices on the shared
 * WorkerField InstancedMesh. `WorkerField.sync()` rewrites every matrix each
 * frame, so `syncEntities()` MUST run AFTER `workerField.sync()` each frame
 * (unhiding relies on the fresh matrix). Instance i corresponds to
 * `workers[i]` (WorkerField.picked = workers.slice(0, CAP)).
 */
import * as THREE from "three";
import type { World } from "./world";
import type { Combatant } from "../sim/combat";
import { updateBuildingView, type BuildingView } from "./buildings";
import type { Worker } from "../sim/workers";
import type { ResourceNode } from "../sim/resources";
import type { NodeField } from "./nodes";

const PLAYER_TEAM = 0;
const ENEMY_TEAM = 1;
/** Ghost opacity factor for explored-but-unseen enemy buildings. */
const GHOST_DIM = 0.45;
/** Mirrors the InstancedMesh capacity in src/render/workers.ts. */
const WORKER_CAP = 160;

/**
 * Structural mirror of the `VisionSystem` contract in `src/sim/vision.ts`
 * (sibling-owned, may not exist yet). The real VisionSystem is assignable
 * to this interface.
 */
export interface VisionLike {
  isVisible(team: 0 | 1, x: number, z: number): boolean;
  exploredAt(team: 0 | 1, x: number, z: number): boolean;
  textureData(): Uint8Array;
  readonly cells: number;
}

export interface FogEntityState {
  fighters: Combatant[];
  buildingViews: BuildingView[];
  workerMesh: THREE.InstancedMesh;
  workers: Worker[];
  nodeField: NodeField;
  nodes: ResourceNode[];
}

const zeroScale = new THREE.Matrix4().makeScale(0, 0, 0);

export class FogOfWarRenderer {
  private readonly fighterShown = new Map<Combatant, boolean>();
  private readonly workerHidden = new Map<Worker, boolean>();
  private readonly ghosted = new Set<BuildingView>();
  private readonly nodeShown = new Map<number, boolean>();
  private readonly nodeGroups = new Map<number, THREE.Object3D>();
  private nodeGroupCount = -1;

  constructor(
    private readonly world: World,
    private readonly vision: VisionLike,
  ) {}

  /**
   * Copy the vision texture into `world.visibilityTex` and flag it for
   * upload. Call after each vision recompute (~2Hz), NOT every frame.
   */
  upload(): void {
    const src = this.vision.textureData();
    const dst = this.world.visibilityTex.image.data as unknown as Uint8Array;
    if (src.length !== dst.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `[fog] vision texture size mismatch (${src.length} vs ${dst.length}); skipping upload`,
      );
      return;
    }
    dst.set(src);
    this.world.visibilityTex.needsUpdate = true;
  }

  /**
   * Apply per-entity visibility for the current frame. Run AFTER
   * `workerField.sync()` and after building view updates — see module note.
   */
  syncEntities(s: FogEntityState): void {
    const v = this.vision;

    // --- Fighters: enemies shown only while currently visible. ---
    const seenFighters = new Set<Combatant>();
    for (const f of s.fighters) {
      seenFighters.add(f);
      const show =
        f.team === PLAYER_TEAM ||
        v.isVisible(PLAYER_TEAM, f.view.anchor.x, f.view.anchor.z);
      if (this.fighterShown.get(f) !== show) {
        this.fighterShown.set(f, show);
        f.view.group.visible = show;
      }
    }
    for (const [f, show] of this.fighterShown) {
      if (!seenFighters.has(f)) {
        this.fighterShown.delete(f);
        if (!show) f.view.group.visible = true;
      }
    }

    // --- Buildings: enemies hidden until explored, ghosted when unseen. ---
    const seenViews = new Set<BuildingView>();
    for (const bv of s.buildingViews) {
      seenViews.add(bv);
      const b = bv.building;
      if (b.team === PLAYER_TEAM) {
        if (this.ghosted.delete(bv)) updateBuildingView(bv);
        if (!bv.group.visible) bv.group.visible = true;
        continue;
      }
      if (!v.exploredAt(PLAYER_TEAM, b.x, b.z)) {
        if (this.ghosted.delete(bv)) updateBuildingView(bv);
        bv.group.visible = false;
        continue;
      }
      if (v.isVisible(PLAYER_TEAM, b.x, b.z)) {
        if (this.ghosted.delete(bv)) updateBuildingView(bv);
        bv.group.visible = true;
        continue;
      }
      // Explored but not currently visible: dim ghost. Reapplied every
      // frame so it self-heals regardless of building-sync call order.
      this.ghosted.add(bv);
      bv.group.visible = true;
      const mat = bv.mesh.material as THREE.MeshStandardMaterial;
      mat.transparent = true;
      mat.opacity =
        (b.state === "active" ? 1 : 0.35 + b.progress * 0.35) * GHOST_DIM;
    }
    for (const bv of [...this.ghosted]) {
      if (!seenViews.has(bv)) {
        this.ghosted.delete(bv);
        updateBuildingView(bv);
        bv.group.visible = true;
      }
    }

    // --- Workers: zero-scale hidden enemy instances. ---
    let workerMatricesDirty = false;
    const count = Math.min(s.workerMesh.count, s.workers.length, WORKER_CAP);
    const seenWorkers = new Set<Worker>();
    for (let i = 0; i < count; i++) {
      const w = s.workers[i];
      seenWorkers.add(w);
      const hide =
        w.team === ENEMY_TEAM && !v.isVisible(PLAYER_TEAM, w.x, w.z);
      if (this.workerHidden.get(w) !== hide) {
        this.workerHidden.set(w, hide);
        if (hide) {
          s.workerMesh.setMatrixAt(i, zeroScale);
          workerMatricesDirty = true;
        }
        // Unhide: WorkerField.sync() already rewrote this instance's matrix
        // this frame (call order), so there is nothing to restore here.
      }
    }
    for (const w of [...this.workerHidden.keys()]) {
      if (!seenWorkers.has(w)) this.workerHidden.delete(w);
    }
    if (workerMatricesDirty) s.workerMesh.instanceMatrix.needsUpdate = true;

    // --- Resource nodes: visible once explored, forever after. ---
    if (s.nodeField.group.children.length !== this.nodeGroupCount) {
      this.nodeGroups.clear();
      for (const child of s.nodeField.group.children) {
        const node = s.nodes.find(
          (n) => n.x === child.position.x && n.z === child.position.z,
        );
        if (node) this.nodeGroups.set(node.id, child);
      }
      this.nodeGroupCount = s.nodeField.group.children.length;
    }
    const seenNodes = new Set<number>();
    for (const n of s.nodes) {
      seenNodes.add(n.id);
      const show = v.exploredAt(PLAYER_TEAM, n.x, n.z);
      if (this.nodeShown.get(n.id) !== show) {
        this.nodeShown.set(n.id, show);
        const g = this.nodeGroups.get(n.id);
        if (g) g.visible = show;
      }
    }
    for (const id of [...this.nodeShown.keys()]) {
      if (!seenNodes.has(id)) this.nodeShown.delete(id);
    }
  }
}
